/**
 * CaptchaWidget Component
 *
 * Modal-based visible CAPTCHA challenge.
 * Renders hCaptcha widget with proper styling and accessibility.
 * Uses dynamic script loading - no npm dependencies.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { CaptchaError, CaptchaErrorCode } from './types';
import { renderVisible, resetWidget, removeWidget, loadHCaptcha } from './hcaptchaLoader';
import {
  CAPTCHA_ERROR_MESSAGES,
  CAPTCHA_Z_INDEX,
  isRecoverableError,
} from './constants';

/**
 * Props for CaptchaWidget
 */
interface CaptchaWidgetProps {
  /** hCaptcha site key from backend */
  siteKey: string;
  /** Called when user successfully completes challenge */
  onVerify: (token: string) => void;
  /** Called when challenge encounters an error */
  onError: (error: CaptchaError) => void;
  /** Called when token expires */
  onExpire: () => void;
  /** Called when user closes the modal */
  onClose: () => void;
  /** Current error to display (from parent) */
  error?: CaptchaError | null;
  /** Theme for hCaptcha widget */
  theme?: 'light' | 'dark';
  /** Size of hCaptcha widget */
  size?: 'normal' | 'compact';
}

/**
 * Create a CaptchaError object
 */
const createError = (
  code: CaptchaErrorCode,
  customMessage?: string
): CaptchaError => ({
  code,
  message: customMessage || CAPTCHA_ERROR_MESSAGES[code],
  recoverable: isRecoverableError(code),
});

/**
 * CaptchaWidget Component
 *
 * Renders a modal with hCaptcha visible widget.
 * Uses portal to render outside of parent DOM hierarchy.
 */
export const CaptchaWidget: React.FC<CaptchaWidgetProps> = ({
  siteKey,
  onVerify,
  onError,
  onExpire,
  onClose,
  error: externalError,
  theme = 'light',
  size = 'normal',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [internalError, setInternalError] = useState<string | null>(null);

  // Combine external and internal errors
  const displayError = externalError?.message || internalError;

  // Generate unique container ID
  const containerId = useRef(`hcaptcha-widget-${Date.now()}`);

  /**
   * Initialize hCaptcha widget
   */
  const initializeWidget = useCallback(async () => {
    try {
      setIsLoading(true);
      setInternalError(null);

      // Load hCaptcha script
      await loadHCaptcha();

      // Wait for container to be in DOM
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!containerRef.current) {
        throw new Error('Container not found');
      }

      // Remove any existing widget
      if (widgetIdRef.current) {
        removeWidget(widgetIdRef.current);
      }

      // Render new widget
      widgetIdRef.current = await renderVisible(
        containerId.current,
        siteKey,
        {
          onVerify: (token) => {
            onVerify(token);
          },
          onError: () => {
            const err = createError('EXECUTION_FAILED');
            setInternalError(err.message);
            onError(err);
          },
          onExpire: () => {
            setInternalError('Verification expired. Please try again.');
            onExpire();
          },
          onClose: undefined, // hCaptcha doesn't have a close callback
        },
        { theme, size }
      );

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to load verification';
      setInternalError(errorMsg);
      onError(createError('LOAD_FAILED', errorMsg));
    }
  }, [siteKey, theme, size, onVerify, onError, onExpire]);

  // Initialize widget on mount
  useEffect(() => {
    initializeWidget();

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current) {
        removeWidget(widgetIdRef.current);
      }
    };
  }, [initializeWidget]);

  // Focus modal on mount
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  /**
   * Handle retry button click
   */
  const handleRetry = useCallback(() => {
    if (widgetIdRef.current) {
      resetWidget(widgetIdRef.current);
    }
    setInternalError(null);
    initializeWidget();
  }, [initializeWidget]);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Render via portal
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: CAPTCHA_Z_INDEX.OVERLAY,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="captcha-title"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ zIndex: CAPTCHA_Z_INDEX.WIDGET }}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
            <svg
              className="h-5 w-5 text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <div>
            <h2
              id="captcha-title"
              className="text-base font-semibold text-gray-900"
            >
              Security Verification
            </h2>
            <p className="text-sm text-gray-500">
              Complete the challenge to continue
            </p>
          </div>
        </div>

        {/* Error message */}
        {displayError && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <div className="flex items-start gap-2">
              <svg
                className="mt-0.5 h-4 w-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <span>{displayError}</span>
                {(externalError?.recoverable || internalError) && (
                  <button
                    onClick={handleRetry}
                    className="ml-2 font-medium text-red-800 underline hover:text-red-900"
                  >
                    Try again
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Widget container */}
        <div className="flex min-h-[78px] justify-center rounded-xl bg-gray-50 p-4">
          {isLoading && !internalError && (
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <svg
                className="h-5 w-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm">Loading verification...</span>
            </div>
          )}

          <div
            id={containerId.current}
            ref={containerRef}
            className={isLoading ? 'hidden' : ''}
          />
        </div>

        {/* Help text */}
        <p className="mt-4 text-center text-xs text-gray-400">
          This helps us prevent automated access and protect your account
        </p>

        {/* Close button */}
        <button
          onClick={onClose}
          className="mt-4 w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
};

export default CaptchaWidget;
